import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  CreditCard, Smartphone, ShieldCheck, Lock, CheckCircle2, Clock, XCircle,
  Loader2, Eye, EyeOff, Snowflake, Send, Wallet, ArrowRight,
  Nfc, Apple, CircleDollarSign, AlertTriangle, Copy, Info
} from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Card3D } from '@/components/Card3D';
import { toast } from 'sonner';
import api from '@/lib/api';

const PHYSICAL_TIERS = [
  { key: 'grey', commission: 7, minLimit: 2000, maxLimit: 5000, color: '#A8B2BD', deepColor: '#5A6474' },
  { key: 'gold', commission: 5, minLimit: 5000, maxLimit: 20000, color: '#D4A843', deepColor: '#A67C2E' },
  { key: 'business', commission: 1, minLimit: 10000, maxLimit: 100000, color: '#2A2E36', deepColor: '#3D4450' },
];

const VIRTUAL_TIERS = [
  { key: 'grey', commission: 7, minLimit: 250, maxLimit: 500, color: '#A8B2BD' },
  { key: 'silver', commission: 5, minLimit: 500, maxLimit: 2000, color: '#C0C8D4' },
  { key: 'platinum', commission: 3, minLimit: 2000, maxLimit: 10000, color: '#E8ECF0' },
];

const STATUS_STEPS = ['pending', 'reviewing', 'approved'];

export default function CardsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [applications, setApplications] = useState([]);
  const [activeCards, setActiveCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [detailCard, setDetailCard] = useState(null);
  const [showCvv, setShowCvv] = useState(false);
  const [freezeLoading, setFreezeLoading] = useState(null);
  const [applyForm, setApplyForm] = useState({ full_name: '', phone: '', delivery_address: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [appsRes, cardsRes] = await Promise.all([
        api.get('/cards/applications'),
        api.get('/cards/active'),
      ]);
      setApplications(appsRes.data.applications || []);
      setActiveCards(cardsRes.data.cards || []);
    } catch (err) {
      console.error('Cards data error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApply = async () => {
    if (!selectedTier) return;
    setApplyLoading(true);
    try {
      await api.post('/cards/apply', {
        card_type: selectedTier.type,
        tier: selectedTier.key,
        full_name: applyForm.full_name || user?.name || '',
        phone: applyForm.phone,
        delivery_address: applyForm.delivery_address,
      });
      toast.success(t('cards.applicationSubmitted'));
      setApplyOpen(false);
      setSelectedTier(null);
      setApplyForm({ full_name: '', phone: '', delivery_address: '' });
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('cards.applicationFailed'));
    } finally {
      setApplyLoading(false);
    }
  };

  const handleFreeze = async (cardId, frozen) => {
    setFreezeLoading(cardId);
    try {
      await api.post(`/cards/${cardId}/freeze`, { frozen });
      toast.success(frozen ? t('cards.cardFrozen') : t('cards.cardActivated'));
      await loadData();
      if (detailCard?.id === cardId) {
        setDetailCard(prev => ({ ...prev, frozen }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setFreezeLoading(null);
    }
  };

  const openApplyDialog = (type, tier) => {
    setSelectedTier({ type, ...tier });
    setApplyForm({ full_name: user?.name || '', phone: '', delivery_address: '' });
    setApplyOpen(true);
  };

  const getStatusProgress = (status) => {
    const idx = STATUS_STEPS.indexOf(status);
    if (status === 'rejected') return 100;
    return idx >= 0 ? ((idx + 1) / STATUS_STEPS.length) * 100 : 0;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-[hsl(43,90%,60%)]" />;
      case 'reviewing': return <Loader2 className="w-4 h-4 text-[hsl(var(--primary))] animate-spin" />;
      case 'approved': return <CheckCircle2 className="w-4 h-4 text-[hsl(160,70%,42%)]" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-[hsl(var(--destructive))]" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatMoney = (v) => `$${Number(v).toLocaleString()}`;

  return (
    <DashboardLayout title={t('cards.title')}>
      <div className="max-w-6xl space-y-10">

        {/* Hero Section */}
        <section className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))]/60 bg-[hsl(222,24%,9%)] p-6 sm:p-10" data-testid="cards-hero">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-[20%] w-[600px] h-[600px] rounded-full bg-[rgba(55,240,196,0.06)] blur-[120px]" />
            <div className="absolute bottom-0 right-[10%] w-[500px] h-[500px] rounded-full bg-[rgba(40,170,255,0.05)] blur-[100px]" />
          </div>
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1 space-y-4">
              <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[hsl(var(--foreground))]">
                {t('cards.heroTitle')}
              </h1>
              <p className="text-base text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
                {t('cards.heroDesc')}
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Badge className="bg-[rgba(55,240,196,0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 gap-1.5 py-1.5 px-3">
                  <Nfc className="w-3.5 h-3.5" /> {t('cards.contactless')}
                </Badge>
                <Badge className="bg-[rgba(55,240,196,0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 gap-1.5 py-1.5 px-3">
                  <Apple className="w-3.5 h-3.5" /> Apple Pay
                </Badge>
                <Badge className="bg-[rgba(55,240,196,0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 gap-1.5 py-1.5 px-3">
                  <Wallet className="w-3.5 h-3.5" /> Google Pay
                </Badge>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Card3D tier="gold" variant="physical" holderName={user?.name || 'CRYPTO USER'} />
            </div>
          </div>
        </section>

        {/* Cards Tabs */}
        <Tabs defaultValue="physical" className="space-y-6">
          <TabsList className="bg-[hsl(var(--secondary))]">
            <TabsTrigger value="physical" data-testid="cards-physical-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-2">
              <CreditCard className="w-4 h-4" /> {t('cards.physicalCards')}
            </TabsTrigger>
            <TabsTrigger value="virtual" data-testid="cards-virtual-tab" className="data-[state=active]:bg-[hsl(var(--card))] gap-2">
              <Smartphone className="w-4 h-4" /> {t('cards.virtualCards')}
            </TabsTrigger>
          </TabsList>

          {/* Physical Cards */}
          <TabsContent value="physical" className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('cards.physicalReq')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {PHYSICAL_TIERS.map((tier) => (
                <Card key={tier.key} className="bg-[hsl(222,20%,12%)] border-[hsl(var(--border))]/60 overflow-hidden hover:border-[hsl(var(--primary))]/30 transition-colors duration-200" data-testid={`cards-physical-tier-${tier.key}`}>
                  <CardContent className="p-5 space-y-4">
                    <Card3D tier={tier.key} variant="physical" mini />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-['Space_Grotesk'] font-semibold text-lg">
                          {t(`cards.physical_${tier.key}`)}
                        </h3>
                        <Badge className="bg-[rgba(55,240,196,0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 text-xs">
                          %{tier.commission}
                        </Badge>
                      </div>
                      <Separator className="bg-[hsl(var(--border))]/40" />
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between text-[hsl(var(--muted-foreground))]">
                          <span>{t('cards.minLimit')}</span>
                          <span className="font-mono text-[hsl(var(--foreground))]">{formatMoney(tier.minLimit)}</span>
                        </div>
                        <div className="flex justify-between text-[hsl(var(--muted-foreground))]">
                          <span>{t('cards.maxLimit')}</span>
                          <span className="font-mono text-[hsl(var(--foreground))]">{formatMoney(tier.maxLimit)}</span>
                        </div>
                        <div className="flex justify-between text-[hsl(var(--muted-foreground))]">
                          <span>{t('cards.commission')}</span>
                          <span className="font-mono text-[hsl(var(--foreground))]">%{tier.commission}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] pt-1">
                        <Lock className="w-3 h-3" />
                        {t('cards.fullKycRequired')}
                      </div>
                    </div>
                    <Button
                      data-testid={`cards-apply-physical-${tier.key}`}
                      className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2"
                      onClick={() => openApplyDialog('physical', tier)}
                    >
                      <Send className="w-4 h-4" /> {t('cards.apply')}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Virtual Cards */}
          <TabsContent value="virtual" className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('cards.virtualReq')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {VIRTUAL_TIERS.map((tier) => (
                <Card key={tier.key} className="bg-[hsl(222,20%,12%)] border-[hsl(var(--border))]/60 overflow-hidden hover:border-[hsl(var(--primary))]/30 transition-colors duration-200" data-testid={`cards-virtual-tier-${tier.key}`}>
                  <CardContent className="p-5 space-y-4">
                    <Card3D tier={tier.key} variant="virtual" mini />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-['Space_Grotesk'] font-semibold text-lg">
                          {t(`cards.virtual_${tier.key}`)}
                        </h3>
                        <Badge className="bg-[rgba(55,240,196,0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 text-xs">
                          %{tier.commission}
                        </Badge>
                      </div>
                      <Separator className="bg-[hsl(var(--border))]/40" />
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between text-[hsl(var(--muted-foreground))]">
                          <span>{t('cards.minLimit')}</span>
                          <span className="font-mono text-[hsl(var(--foreground))]">{formatMoney(tier.minLimit)}</span>
                        </div>
                        <div className="flex justify-between text-[hsl(var(--muted-foreground))]">
                          <span>{t('cards.maxLimit')}</span>
                          <span className="font-mono text-[hsl(var(--foreground))]">{formatMoney(tier.maxLimit)}</span>
                        </div>
                        <div className="flex justify-between text-[hsl(var(--muted-foreground))]">
                          <span>{t('cards.commission')}</span>
                          <span className="font-mono text-[hsl(var(--foreground))]">%{tier.commission}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Badge className="text-[10px] bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] gap-1" data-testid="cards-wallet-applepay-badge">
                          <Apple className="w-3 h-3" /> Apple Pay
                        </Badge>
                        <Badge className="text-[10px] bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] gap-1" data-testid="cards-wallet-googlepay-badge">
                          <Wallet className="w-3 h-3" /> Google Pay
                        </Badge>
                        <Badge className="text-[10px] bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] gap-1">
                          <Nfc className="w-3 h-3" /> {t('cards.contactless')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] pt-1">
                        <ShieldCheck className="w-3 h-3" />
                        {t('cards.email2faRequired')}
                      </div>
                    </div>
                    <Button
                      data-testid={`cards-apply-virtual-${tier.key}`}
                      className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2"
                      onClick={() => openApplyDialog('virtual', tier)}
                    >
                      <Send className="w-4 h-4" /> {t('cards.apply')}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Active Cards Section */}
        <section className="space-y-4" data-testid="cards-active-section">
          <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[hsl(var(--primary))]" />
            {t('cards.activeCards')}
          </h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <Card key={i} className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <CardContent className="p-5 space-y-3">
                    <Skeleton className="h-40 rounded-xl" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : activeCards.length === 0 ? (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardContent className="py-12 text-center space-y-3">
                <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--secondary))] flex items-center justify-center mx-auto">
                  <CreditCard className="w-8 h-8 text-[hsl(var(--muted-foreground))]" />
                </div>
                <p className="font-medium">{t('cards.noActiveCards')}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('cards.noActiveCardsDesc')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeCards.map((card) => (
                <Card
                  key={card.id}
                  data-testid={`cards-active-${card.id}`}
                  className={`bg-[hsl(222,20%,12%)] border-[hsl(var(--border))]/60 cursor-pointer hover:border-[hsl(var(--primary))]/30 transition-colors duration-200 ${card.frozen ? 'opacity-60' : ''}`}
                  onClick={() => { setDetailCard(card); setShowCvv(false); }}
                >
                  <CardContent className="p-5 space-y-3">
                    <Card3D tier={card.tier} variant={card.card_type} mini />
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm">{card.tier_name}</h4>
                        {card.frozen ? (
                          <Badge className="text-[10px] bg-[rgba(40,170,255,0.1)] text-[#28AAFF] border-[#28AAFF]/20 gap-1">
                            <Snowflake className="w-3 h-3" /> {t('cards.frozen')}
                          </Badge>
                        ) : (
                          <Badge className="text-[10px] bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20 gap-1">
                            <CheckCircle2 className="w-3 h-3" /> {t('common.active')}
                          </Badge>
                        )}
                      </div>
                      <p className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{card.card_number_masked}</p>
                      <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
                        <span>{t('cards.monthlySpent')}</span>
                        <span className="font-mono">{formatMoney(card.monthly_spent)} / {formatMoney(card.monthly_limit)}</span>
                      </div>
                      <Progress value={(card.monthly_spent / card.monthly_limit) * 100} className="h-1.5" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Applications Section */}
        {applications.length > 0 && (
          <section className="space-y-4" data-testid="cards-applications-section">
            <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5 text-[hsl(43,90%,60%)]" />
              {t('cards.applicationStatus')}
            </h2>
            <div className="space-y-3">
              {applications.map((app) => (
                <Card key={app.id} className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid={`cards-application-${app.id}`}>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        {getStatusIcon(app.status)}
                        <div>
                          <p className="font-medium text-sm">{app.tier_name}</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">
                            {app.card_type === 'physical' ? t('cards.physicalCard') : t('cards.virtualCard')} - %{app.commission}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge data-testid="cards-application-status-step" className={`text-xs gap-1 ${
                          app.status === 'approved' ? 'bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20' :
                          app.status === 'rejected' ? 'bg-[rgba(217,74,74,0.1)] text-[#D94A4A] border-[#D94A4A]/20' :
                          app.status === 'reviewing' ? 'bg-[rgba(55,240,196,0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20' :
                          'bg-[rgba(247,200,75,0.1)] text-[hsl(43,90%,60%)] border-[hsl(43,90%,60%)]/20'
                        }`}>
                          {t(`cards.status_${app.status}`)}
                        </Badge>
                        <Progress value={getStatusProgress(app.status)} className="w-20 h-1.5" />
                      </div>
                    </div>
                    {/* Status Timeline */}
                    {app.status_history && app.status_history.length > 0 && (
                      <div className="mt-3 pl-7 border-l border-[hsl(var(--border))]/40 space-y-2">
                        {app.status_history.map((step, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] -ml-[calc(0.75rem+1px)]" />
                            <span className="text-[hsl(var(--muted-foreground))]">{step.note}</span>
                            <span className="text-[hsl(var(--muted-foreground))]/60 font-mono">
                              {new Date(step.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Apply Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[hsl(var(--primary))]" />
              {t('cards.applyTitle')}
            </DialogTitle>
            <DialogDescription className="text-[hsl(var(--muted-foreground))]">
              {selectedTier && (
                <span>{t(`cards.${selectedTier.type === 'physical' ? 'physical' : 'virtual'}_${selectedTier.key}`)} - %{selectedTier.commission} {t('cards.commission')}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedTier && (
            <div className="space-y-4">
              {/* Card Preview */}
              <div className="flex justify-center py-2">
                <Card3D tier={selectedTier.key} variant={selectedTier.type} mini holderName={applyForm.full_name || user?.name || 'YOUR NAME'} />
              </div>

              {/* Limits Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))] text-center">
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('cards.minLimit')}</p>
                  <p className="font-mono font-semibold text-sm">{formatMoney(selectedTier.minLimit)}</p>
                </div>
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))] text-center">
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('cards.maxLimit')}</p>
                  <p className="font-mono font-semibold text-sm">{formatMoney(selectedTier.maxLimit)}</p>
                </div>
              </div>

              {/* Verification Requirements */}
              <div className="p-3 rounded-lg bg-[rgba(55,240,196,0.05)] border border-[hsl(var(--primary))]/10">
                <div className="flex items-center gap-2 text-sm">
                  <Info className="w-4 h-4 text-[hsl(var(--primary))]" />
                  <span className="text-[hsl(var(--muted-foreground))]">
                    {selectedTier.type === 'physical' ? t('cards.fullKycRequired') : t('cards.email2faRequired')}
                  </span>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('cards.fullName')}</Label>
                  <Input
                    data-testid="cards-apply-name-input"
                    value={applyForm.full_name}
                    onChange={(e) => setApplyForm(prev => ({ ...prev, full_name: e.target.value }))}
                    className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"
                    placeholder={user?.name || ''}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('cards.phone')}</Label>
                  <Input
                    data-testid="cards-apply-phone-input"
                    value={applyForm.phone}
                    onChange={(e) => setApplyForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"
                    placeholder="+90 5XX XXX XX XX"
                  />
                </div>
                {selectedTier.type === 'physical' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t('cards.deliveryAddress')}</Label>
                    <Input
                      data-testid="cards-apply-address-input"
                      value={applyForm.delivery_address}
                      onChange={(e) => setApplyForm(prev => ({ ...prev, delivery_address: e.target.value }))}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"
                      placeholder={t('cards.addressPlaceholder')}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApplyOpen(false)}>{t('common.cancel')}</Button>
            <Button
              data-testid="cards-apply-button"
              disabled={applyLoading || !applyForm.full_name}
              onClick={handleApply}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2"
            >
              {applyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t('cards.submitApplication')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card Detail Sheet */}
      <Sheet open={!!detailCard} onOpenChange={(open) => { if (!open) { setDetailCard(null); setShowCvv(false); } }}>
        <SheetContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-full sm:max-w-md overflow-y-auto">
          {detailCard && (
            <>
              <SheetHeader>
                <SheetTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[hsl(var(--primary))]" />
                  {detailCard.tier_name}
                </SheetTitle>
                <SheetDescription className="text-[hsl(var(--muted-foreground))]">
                  {detailCard.card_type === 'physical' ? t('cards.physicalCard') : t('cards.virtualCard')}
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                {/* Card Visual */}
                <Card3D tier={detailCard.tier} variant={detailCard.card_type} cardNumber={detailCard.card_number_masked} holderName={user?.name} expiry={detailCard.expiry_date} />

                {/* Card Details */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-sm text-[hsl(var(--muted-foreground))]">{t('cards.cardNumber')}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{detailCard.card_number_masked}</span>
                      <button onClick={() => { navigator.clipboard.writeText(detailCard.card_number_masked); toast.success(t('common.copy')); }}>
                        <Copy className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-sm text-[hsl(var(--muted-foreground))]">{t('cards.expiry')}</span>
                    <span className="font-mono text-sm">{detailCard.expiry_date}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-sm text-[hsl(var(--muted-foreground))]">CVV</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{showCvv ? detailCard.cvv_hash : '***'}</span>
                      <button
                        data-testid="cards-cvv-toggle"
                        onClick={() => setShowCvv(!showCvv)}
                        className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                      >
                        {showCvv ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-[hsl(var(--secondary))]">
                    <span className="text-sm text-[hsl(var(--muted-foreground))]">{t('cards.commission')}</span>
                    <span className="font-mono text-sm">%{detailCard.commission}</span>
                  </div>
                </div>

                {/* Spending */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[hsl(var(--muted-foreground))]">{t('cards.monthlySpent')}</span>
                    <span className="font-mono">{formatMoney(detailCard.monthly_spent)} / {formatMoney(detailCard.monthly_limit)}</span>
                  </div>
                  <Progress value={(detailCard.monthly_spent / detailCard.monthly_limit) * 100} className="h-2" />
                </div>

                {/* Digital Wallets */}
                {detailCard.card_type === 'virtual' && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">{t('cards.digitalWallets')}</p>
                    <div className="flex gap-2">
                      {detailCard.supports_apple_pay && (
                        <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] gap-1.5 py-1.5 px-3">
                          <Apple className="w-4 h-4" /> Apple Pay
                        </Badge>
                      )}
                      {detailCard.supports_google_pay && (
                        <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] gap-1.5 py-1.5 px-3">
                          <Wallet className="w-4 h-4" /> Google Pay
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <Separator className="bg-[hsl(var(--border))]/40" />

                {/* Freeze Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))]">
                  <div className="flex items-center gap-2">
                    <Snowflake className="w-4 h-4 text-[#28AAFF]" />
                    <span className="text-sm">{t('cards.freezeCard')}</span>
                  </div>
                  <Switch
                    data-testid="cards-freeze-toggle"
                    checked={detailCard.frozen}
                    onCheckedChange={(checked) => handleFreeze(detailCard.id, checked)}
                    disabled={freezeLoading === detailCard.id}
                  />
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </DashboardLayout>
  );
}
